import { Breadcrumb } from '@/components/ui/Breadcrumb';
import React, { useState, useEffect } from 'react';
import { BlogPost } from '@/types';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/Button';
import { calculateReadingTime } from '@/lib/utils';
import { searchIndex } from '@/lib/search';
import { Search } from 'lucide-react';
import { seedPost } from '@/data/seedBlogPost';
import { useInitialData } from '@/contexts/InitialDataContext';

export function BlogPage() {
  const { formatDate } = useRegion();
  const initialData = useInitialData();

  const [posts, setPosts] = useState<BlogPost[]>(() => {
    if (initialData?.posts && initialData.posts.length > 0) {
      return initialData.posts;
    }
    return [];
  });
  const [loading, setLoading] = useState(() => {
    if (initialData?.posts && initialData.posts.length > 0) {
      return false;
    }
    return true;
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (initialData?.posts && initialData.posts.length > 0) {
      return;
    }
    async function fetchPosts() {
      try {
        const res = await fetch('/api/public/posts');
        if (res.ok) {
          const fetchedPosts = await res.json() as BlogPost[];
          if (Array.isArray(fetchedPosts) && fetchedPosts.length > 0) {
            fetchedPosts.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            setPosts(fetchedPosts);
          } else {
            setPosts([{
              ...seedPost,
              id: 'default-seed-post',
              createdAt: Date.now(),
              updatedAt: Date.now(),
              authorId: 'admin',
              authorName: 'Colin',
              published: true
            } as BlogPost]);
          }
        }
      } catch (error) {
        console.warn('Error fetching blog posts:', error);
        setPosts([{
          ...seedPost,
          id: 'default-seed-post',
          createdAt: Date.now(),
          updatedAt: Date.now(),
          authorId: 'admin',
          authorName: 'Colin',
          published: true
        } as BlogPost]);
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
  }, [initialData]);

  const filteredPosts = searchIndex(posts, searchQuery, ['title', 'excerpt', 'content', 'authorName']);

  return (
    <div className="min-h-screen bg-ground pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Breadcrumb items={[{ label: 'Insights & Articles' }]} />
        <div className="mb-12">
          <h1 className="text-5xl md:text-6xl font-serif text-ink mb-6 leading-tight">
            Insights & Perspectives
          </h1>
          <p className="text-xl text-ink/70/80 max-w-2xl font-light">
            Thoughts, frameworks, and stories on people development, Enneagram practice, and leadership from the FIVE LENS team.
          </p>
        </div>

        <div className="mb-12 max-w-md relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/70/50" />
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-ground-deep bg-white focus:outline-none focus:border-rule-3 text-sm transition-colors"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-ground-deep flex flex-col h-full animate-pulse">
                <div className="h-48 bg-ground-deep/50 w-full"></div>
                <div className="p-8 flex flex-col flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-3 bg-ground-deep/50 rounded w-24"></div>
                    <div className="w-1 h-1 rounded-full bg-ground-deep/50"></div>
                    <div className="h-3 bg-ground-deep/50 rounded w-16"></div>
                  </div>
                  <div className="h-6 bg-ground-deep/50 rounded w-3/4 mb-4"></div>
                  <div className="h-6 bg-ground-deep/50 rounded w-1/2 mb-6"></div>
                  
                  <div className="space-y-2 mb-6">
                    <div className="h-3 bg-ground-deep/50 rounded w-full"></div>
                    <div className="h-3 bg-ground-deep/50 rounded w-full"></div>
                    <div className="h-3 bg-ground-deep/50 rounded w-4/5"></div>
                  </div>
                  
                  <div className="mt-auto pt-6 border-t border-ground-deep flex justify-between">
                    <div className="h-4 bg-ground-deep/50 rounded w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.length === 0 ? (
              <div className="col-span-full py-16 text-center text-ink/70/60">
                <p className="text-lg">No posts found matching your search.</p>
                {searchQuery && (
                   <button onClick={() => setSearchQuery('')} className="mt-2 text-rule-3 font-bold hover:underline text-sm uppercase tracking-wider">
                     Clear Search
                   </button>
                )}
              </div>
            ) : (
              filteredPosts.map((post, i) => (
                <motion.article 
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-white border border-ground-deep hover:shadow-lg hover:shadow-ink/10 hover:border-ink hover:-translate-y-1 transition-all duration-300 flex flex-col h-full group"
                >
                  {post.imageUrl && (
                    <div className="h-48 overflow-hidden bg-ground/50">
                      <LazyImage 
                        src={post.imageUrl} 
                        alt={post.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                      />
                    </div>
                  )}
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-3 text-xs font-bold tracking-widest text-rule-3 uppercase mb-4">
                      <span>{formatDate(post.createdAt, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      <span className="w-1 h-1 rounded-full bg-rule-3/40"></span>
                      <span>{calculateReadingTime(post)}</span>
                    </div>
                    <h2 className="text-2xl font-serif text-ink mb-4 group-hover:text-rule-3 transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-ink/70/70 line-clamp-3 mb-6 font-light">
                      {post.excerpt || (post.content ? post.content.replace(/<[^>]*>?/gm, '').substring(0, 150) + '...' : '')}
                    </p>
                    <div className="mt-auto pt-6 border-t border-ground-deep">
                      
                    <div className="flex items-center justify-between w-full">
                      <Link to={`/blog/${post.id}`}>
                        <span className="text-sm font-bold uppercase tracking-widest text-ink/70 group-hover:text-rule-3 transition-colors flex items-center gap-2">
                          Read Article <span className="text-xl leading-none">→</span>
                        </span>
                      </Link>
                    </div>

                    </div>
                  </div>
                </motion.article>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
import { useRegion } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';
